/*
  Aggregates a report's feedback responses into per-question answer counts.

  The Video Logs detail pane can show several responses at once, where one card
  per response gets long fast. The QR Scan Analytics drawer still lists them
  individually through FeedbackResponseCard, which this deliberately leaves
  alone.

  Responses are anonymous. A comment carries its 1-based position in the
  response list and nothing else — the same numbering FeedbackResponseCard
  prints, so both views mean the same thing by "response 3".
*/

/*
  Kept in step with the questions in src/components/FeedbackResponseCard.jsx.
  The null label differs per question on purpose: an unanswered
  illustrations/recommend question means "not applicable", while an unanswered
  SPE question means the patient never read it. Collapsing the two into one
  "no answer" bucket would lose that.
*/
export const feedbackQuestions = Object.freeze([
  {
    key: "IllustrationsHelpful",
    label: "Illustrations helped understanding",
    nullLabel: "n/a",
  },
  {
    key: "RecommendReport",
    label: "Would recommend this report",
    nullLabel: "n/a",
  },
  {
    key: "SpeHelpful",
    label: "Simplified explanation helpful",
    nullLabel: "didn't read",
  },
])

export const deliveryQuestionLabel = "How they received their report"

// The options the patient is offered, in the order they see them — see the form
// in src/pages/EducationalVideos/index.jsx.
export const reportDeliveryLabels = Object.freeze({
  "Doctor's office": "office",
  Email: "email",
  "Patient portal": "portal",
  Other: "other",
})

/*
  Duplicated from `answerLabel` in src/components/FeedbackResponseCard.jsx
  rather than imported, so that shared component stays untouched while this page
  is restructured. Worth collapsing to one source once both views have settled.
*/
export const answerTones = Object.freeze({
  positive: "text-green-700 bg-green-50 border-green-200",
  negative: "text-red-700 bg-red-50 border-red-200",
  neutral: "text-gray-500 bg-gray-50 border-gray-200",
})

const NOT_ANSWERED = "not answered"
const deliveryOrder = Object.keys(reportDeliveryLabels)

const countTriState = (responses, key) =>
  responses.reduce(
    (counts, response) => {
      if (response[key] === true) counts.yes += 1
      else if (response[key] === false) counts.no += 1
      else counts.unanswered += 1
      return counts
    },
    { yes: 0, no: 0, unanswered: 0 }
  )

// Fixed order, never by count: a question whose pills reorder between records
// cannot be compared positionally while scanning down the list.
const triStateAnswers = ({ yes, no, unanswered }, nullLabel) =>
  [
    { key: "yes", label: "yes", count: yes, tone: "positive" },
    { key: "no", label: "no", count: no, tone: "negative" },
    { key: "unanswered", label: nullLabel, count: unanswered, tone: "neutral" },
  ].filter((answer) => answer.count > 0)

// Ranked by the order the patient saw the options, then anything unrecognised,
// then "not answered" last.
const deliveryRank = (key) => {
  if (key === NOT_ANSWERED) return deliveryOrder.length + 1
  const index = deliveryOrder.indexOf(key)
  return index === -1 ? deliveryOrder.length : index
}

const deliveryAnswers = (responses) => {
  const counts = new Map()

  for (const response of responses) {
    // An unrecognised value is counted under itself rather than dropped, so an
    // option added to the patient form still shows up here.
    const key = response.ReportDelivery?.trim() || NOT_ANSWERED
    counts.set(key, (counts.get(key) || 0) + 1)
  }

  return [...counts.entries()]
    .sort(([a], [b]) => deliveryRank(a) - deliveryRank(b) || a.localeCompare(b))
    .map(([key, count]) => ({
      key,
      label: reportDeliveryLabels[key] || key.toLowerCase(),
      count,
      // A delivery method has no good/bad reading, so colouring it would imply
      // one. Same reasoning as the plain-text treatment in
      // FeedbackResponseCard.
      tone: "neutral",
    }))
}

export function aggregateFeedback(responses = []) {
  return {
    total: responses.length,
    questions: feedbackQuestions.map(({ key, label, nullLabel }) => ({
      key,
      label,
      answers: triStateAnswers(countTriState(responses, key), nullLabel),
    })),
    delivery: {
      key: "ReportDelivery",
      label: deliveryQuestionLabel,
      answers: deliveryAnswers(responses),
    },
    comments: responses
      .map((response, index) => ({
        id: response.CustomerFeedbackId,
        responseNumber: index + 1,
        text: response.Comments?.trim(),
      }))
      .filter((comment) => comment.text),
  }
}
